import { useState, useEffect, useCallback, useRef, useTransition } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { apiClient, useAuthStore, useAppStore } from '@indipia/shared';
import { Button, DateRangePicker, FileInput, FilterChip, Search, type DateRangeValue, ScrollTopButton } from '@indipia/ui-core';
import { SitePanel } from './SitePanel';
import { PhotoCard } from '../components/PhotoCard';
import { photoApi } from '../api/photoApi';
import type { TagEditSiteGroup } from '../api/photoApi';
import { usePhotoNavStore } from '../store/photoNavStore';
import { usePhotoCommonStore } from '../store/photoCommonStore';
import type { ShareSelectedPhoto, ShareSiteInfo } from '../types/photo';
import { useTagCodeMaps } from '../utils/tagCodeMap';
import { useRightSlot } from '../hooks/useRightSlot';
import { useEnsurePhotoCommonCodes } from '../hooks/useEnsurePhotoCommonCodes';

const BASE = '/agents/photo-organizer';
const UNTAGGED_TAG = '태그없음';

// selected: '전체'를 제외한 선택된 태그 집합 — 빈 집합이면 '전체'(필터 없음)를 의미한다.
// 태그없음(UNTAGGED_TAG)과 일반 태그가 동시에 선택되면 백엔드가 OR로 합쳐서 반환한다.
const tagFilterParams = (selected: Set<string>): Record<string, unknown> => {
  const spaceTags = [...selected].filter(t => t !== UNTAGGED_TAG);
  const params: Record<string, unknown> = {};
  if (spaceTags.length) params.spaceTag = spaceTags.join(',');
  if (selected.has(UNTAGGED_TAG)) params.untagged = true;
  return params;
};

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp']);
const ALLOWED_EXT = new Set(['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp']);
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const isAllowedFile = (f: File) => {
  if (ALLOWED_MIME.has(f.type)) return true;
  const ext = f.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
  return ALLOWED_EXT.has(ext);
};

const sha256Hex = async (file: File): Promise<string> => {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
};

interface SiteDetailCard {
  siteId: number;
  siteName: string;
  siteStatus: string | null;
  siteStatusName: string | null;
  customerName: string | null;
  customerMobile: string | null;
  address: string | null;
  photoCount: number;
  spaceTagCount: number;
  untaggedPhotoCount: number;
}

interface SiteTagItem {
  tagType: string;
  tagValue: string;
  photoCount: number;
}

interface SitePhotoTag {
  tagType: string;
  tagValue: string;
  tagCode?: string | null;
}

interface SitePhotoItem {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
  takenAt: string | null;
  registeredAt: string | null;
  uploadedAt: string | null;
  spaceTag: string | null;
  workTypeTag: string | null;
  tags: SitePhotoTag[];
  memo: string | null;
}

interface SitePhotoPageResponse {
  totalCount: number;
  hasNext: boolean;
  photos: SitePhotoItem[];
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string }> = {
  ACTIVE: { label: '진행중', color: 'var(--color-success)', bg: 'color-mix(in srgb, var(--color-success) 12%, transparent)', border: 'color-mix(in srgb, var(--color-success) 35%, transparent)' },
  DONE:   { label: '완료',   color: 'var(--shell-on-surface-2)', bg: 'color-mix(in srgb, var(--shell-on-surface-2) 10%, transparent)',  border: 'color-mix(in srgb, var(--shell-on-surface-2) 30%, transparent)'  },
};

const THUMB_COLORS = [
  'var(--gt-subtle)',
  'var(--shell-skeleton)',
  'var(--shell-bg)',
  'var(--shell-surface)',
  'var(--shell-skeleton)',
  'var(--gt-subtle)',
];


function fmtShortDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

// DateRangePicker는 시각 없는 자정 기준 Date를 준다 — end 경계는 그날 마지막 순간까지 포함해야 한다.
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

const EMPTY_DATE_RANGE: DateRangeValue = { start: null, end: null };

const LIMIT = 20;

export interface SiteDetailMainProps {
  /** 스튜디오 홈 "현장 작업물 슬라이드오버"에서 넘겨준다 — 지정되면 라우트 파라미터 대신 이 값을 쓴다. */
  siteId?: number;
  /** true면 독립 라우트가 아니라 다른 화면(슬라이드오버)에 끼워 넣은 상태 — 우측 현장 전환
   *  패널·목록으로의 "뒤로" 이동을 끄고, 닫기는 onClose로 위임한다. */
  embedded?: boolean;
  onClose?: () => void;
}

export function SiteDetailMain({ siteId: siteIdProp, embedded = false, onClose }: SiteDetailMainProps = {}) {
  const navigate = useNavigate();
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = siteIdProp ?? (siteIdParam ? Number(siteIdParam) : null);
  // embedded일 땐 PhotoOrganizerLayout이 조상에 없을 수 있어 rightSlot을 그릴 곳이 없다 — 끈다.
  useRightSlot(!embedded && siteId ? <SitePanel currentSiteId={siteId} /> : null, [siteId, embedded]);
  // embedded로 단독 마운트되면 PhotoOrganizerLayout의 공용코드 로딩 effect를 안 거치므로 직접 보장한다
  // (이미 로드돼 있으면 훅 내부에서 재조회를 건너뛴다).
  useEnsurePhotoCommonCodes();
  const setShareSend      = usePhotoNavStore(s => s.setShareSend);
  const setPhotoDetailCtx = usePhotoNavStore(s => s.setPhotoDetailCtx);
  const sharePhotos       = usePhotoNavStore(s => s.sharePhotos);
  const preSelectShare   = usePhotoNavStore(s => s.preSelectShare);
  const setPreSelectShare = usePhotoNavStore(s => s.setPreSelectShare);
  const setTagEditGroups        = usePhotoNavStore(s => s.setTagEditGroups);
  const setTagBulkEditSavedTags = usePhotoNavStore(s => s.setTagBulkEditSavedTags);
  const setTagEditReturnPath    = usePhotoNavStore(s => s.setTagEditReturnPath);

  const user = useAuthStore(s => s.user);
  const commonCodes = usePhotoCommonStore(s => s.commonCodes);
  const appCodes    = useAppStore(s => s.codes);
  const { spaceCodeByName, workCodeByName } = useTagCodeMaps();
  const userTags = usePhotoCommonStore(s => s.userTags);
  const userTagIdByName = Object.fromEntries(userTags.map(u => [u.tagValue, u.userTagId]));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiBase: string = ((import.meta as any).env ?? {}).VITE_API_BASE ?? '';
  const photoBase = apiBase.includes('localhost') ? `${apiBase}/photo` : '/photo';

  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [takenDateRange, setTakenDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [regDateRange, setRegDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [memoQuery, setMemoQuery] = useState('');
  const [memoFilter, setMemoFilter] = useState<'all' | 'has' | 'none'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [movingToTagEdit, setMovingToTagEdit] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [detail, setDetail] = useState<SiteDetailCard | null>(null);
  const [tags, setTags] = useState<SiteTagItem[]>([]);
  const [photos, setPhotos] = useState<SitePhotoItem[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [offset, setOffset] = useState(0);
  // 태그 필터 전환·무한스크롤·현장 전환이 모두 같은 "/photos" 목록을 갱신한다 — 이 셋 중 나중에
  // 시작한 요청이 아니라 나중에 "도착"한 응답이 이기면 사진마다 태그가 붙었다 안 붙었다 하는 것처럼
  // 보인다(예: 필터를 빠르게 두 번 바꾸면 먼저 보낸 요청이 늦게 도착해 최신 상태를 덮어씀).
  // 세 진입점이 항상 이 ref 하나를 공유해 이전 요청을 abort하도록 해서, 가장 마지막에 "시작한"
  // 요청의 응답만 반영되도록 강제한다.
  const photoFetchAbortRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isPending, startTransition] = useTransition();
  // 우측 패널에서 다른 현장으로 이동할 때 — 최초 진입(loading)과 달리 이전 현장 화면을 그대로 둔 채 옅게 dim만 시켜
  // detail/photos를 비웠다가 다시 채우는 과정에서 생기는 깜박임을 없앤다.
  const [switchingSite, setSwitchingSite] = useState(false);

  useEffect(() => {
    if (!siteId) { setLoading(false); return; }

    photoFetchAbortRef.current?.abort();
    const controller = new AbortController();
    photoFetchAbortRef.current = controller;
    const { signal } = controller;
    const isFirstLoad = detail === null;

    if (isFirstLoad) {
      setLoading(true);
      setFetchError(false);
    } else {
      setSwitchingSite(true);
    }
    setSelectedTags(new Set());
    setTakenDateRange(EMPTY_DATE_RANGE);
    setRegDateRange(EMPTY_DATE_RANGE);
    setMemoQuery('');
    setMemoFilter('all');
    setSelectedIds(new Set());

    Promise.all([
      apiClient.get(`${photoBase}/sites/${siteId}/detail`, { signal }),
      apiClient.get(`${photoBase}/sites/${siteId}/tags`, { signal }),
      apiClient.get(`${photoBase}/sites/${siteId}/photos`, { params: { limit: LIMIT, offset: 0 }, signal }),
    ])
      .then(([detailRes, tagsRes, photosRes]) => {
        setDetail(detailRes.data?.data ?? null);
        setTags(tagsRes.data?.data ?? []);
        const pData: SitePhotoPageResponse = photosRes.data?.data;
        const loaded: SitePhotoItem[] = pData?.photos ?? [];
        setPhotos(loaded);
        setHasNext(pData?.hasNext ?? false);
        setOffset(loaded.length);
        if (preSelectShare && sharePhotos.length > 0) {
          setSelectedIds(new Set(sharePhotos.map(p => p.id)));
          setPreSelectShare(false);
        }
      })
      .catch(err => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') setFetchError(true); })
      .finally(() => { setLoading(false); setSwitchingSite(false); });

    return () => controller.abort();
  // preSelectShare/sharePhotos/detail intentionally excluded — apply only on mount/siteId change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const toggleTag = (tag: string) => {
    if (!siteId) return;
    let next: Set<string>;
    if (tag === '전체') {
      if (selectedTags.size === 0) return;
      next = new Set();
    } else {
      next = new Set(selectedTags);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
    }
    setSelectedTags(next);
    setOffset(0);
    setHasNext(false);
    setSelectedIds(new Set());
    photoFetchAbortRef.current?.abort();
    const controller = new AbortController();
    photoFetchAbortRef.current = controller;
    const params: Record<string, unknown> = { limit: LIMIT, offset: 0, ...tagFilterParams(next) };
    apiClient.get(`${photoBase}/sites/${siteId}/photos`, { params, signal: controller.signal })
      .then(res => {
        const pData: SitePhotoPageResponse = res.data?.data;
        const loaded: SitePhotoItem[] = pData?.photos ?? [];
        startTransition(() => {
          setPhotos(loaded);
          setHasNext(pData?.hasNext ?? false);
          setOffset(loaded.length);
        });
      })
      .catch(err => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') console.error(err); });
  };

  const loadMore = useCallback(() => {
    if (!siteId || loadingMore || !hasNext || switchingSite) return;
    setLoadingMore(true);
    photoFetchAbortRef.current?.abort();
    const controller = new AbortController();
    photoFetchAbortRef.current = controller;
    const params: Record<string, unknown> = { limit: LIMIT, offset, ...tagFilterParams(selectedTags) };
    apiClient.get(`${photoBase}/sites/${siteId}/photos`, { params, signal: controller.signal })
      .then(res => {
        const pData: SitePhotoPageResponse = res.data?.data;
        const loaded: SitePhotoItem[] = pData?.photos ?? [];
        setPhotos(prev => {
          const seen = new Set(prev.map(p => p.photoId));
          return [...prev, ...loaded.filter(p => !seen.has(p.photoId))];
        });
        setHasNext(pData?.hasNext ?? false);
        setOffset(prev => prev + loaded.length);
      })
      .catch(err => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') console.error(err); })
      .finally(() => setLoadingMore(false));
  }, [siteId, loadingMore, hasNext, offset, selectedTags, photoBase, switchingSite]);

  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleAddPhotos = async (selected: File[]) => {
    if (!selected.length || !user || !siteId) return;

    // 1. 미지원 확장자 체크
    const typed = selected.filter(isAllowedFile);
    const rejected = selected.filter(f => !isAllowedFile(f));
    if (rejected.length) {
      const preview = rejected.slice(0, 3).map(f => f.name).join(', ');
      const suffix = rejected.length > 3 ? ` 외 ${rejected.length - 3}건` : '';
      toast.error(`허용되지 않는 확장자입니다: ${preview}${suffix}`);
    }
    if (!typed.length) return;

    // 2. 20MB 초과 파일 체크
    const oversized = typed.filter(f => f.size > MAX_FILE_BYTES);
    if (oversized.length) {
      const preview = oversized.slice(0, 3).map(f => f.name).join(', ');
      const suffix = oversized.length > 3 ? ` 외 ${oversized.length - 3}건` : '';
      toast.error(`20MB 초과 파일은 업로드할 수 없습니다: ${preview}${suffix}`);
    }
    let imgs = typed.filter(f => f.size <= MAX_FILE_BYTES);
    if (!imgs.length) return;

    setUploading(true);
    try {
      // 3. 타 현장 중복 해시 체크
      try {
        const fileHashes = await Promise.all(imgs.map(sha256Hex));
        const { data } = await photoApi.checkDuplicates(fileHashes);
        const { matchedCount, results } = data.data;
        if (matchedCount > 0) {
          const matchedFiles = imgs.filter((_, i) => results[i]?.photoId !== null);
          const preview = matchedFiles.slice(0, 3).map(f => f.name).join(', ');
          const suffix = matchedFiles.length > 3 ? ` 외 ${matchedFiles.length - 3}건` : '';
          const matchedSites = [...new Set(results.map(r => r.siteName).filter((s): s is string => s !== null))];
          const siteStr = matchedSites.length ? ` (${matchedSites.join(', ')})` : '';
          toast.error(`등록된 사진은 업로드할 수 없습니다.: ${preview}${suffix}${siteStr}`);
          imgs = imgs.filter((_, i) => results[i]?.photoId === null);
          if (!imgs.length) return;
        }
      } catch {
        // 중복 확인 실패 시 무시하고 계속 진행
      }

      // 세션은 서버가 업로드 안에서 생성한다(U3) — 현장 지정 업로드라 세션 반환은 사용하지 않음
      await photoApi.uploadBatch(imgs, { siteId });
      toast.success(`${imgs.length}장 업로드 완료`);

      const [detailRes, photosRes] = await Promise.all([
        apiClient.get(`${photoBase}/sites/${siteId}/detail`),
        apiClient.get(`${photoBase}/sites/${siteId}/photos`, { params: { limit: LIMIT, offset: 0 } }),
      ]);
      setDetail(detailRes.data?.data ?? null);
      const pData: SitePhotoPageResponse = photosRes.data?.data;
      setPhotos(pData?.photos ?? []);
      setHasNext(pData?.hasNext ?? false);
      setOffset(pData?.photos?.length ?? 0);
      setSelectedTags(new Set());
    } catch {
      toast.error('업로드에 실패했습니다');
    } finally {
      setUploading(false);
    }
  };

  const addPhotoTag = async (photoId: number, tagValue: string) => {
    const val = tagValue.trim();
    if (!val) return;

    const photo = photos.find(p => p.photoId === photoId);
    if (!photo) return;
    if (photo.tags.some(t => t.tagValue === val)) return;
    if (photo.tags.length >= 10) { toast.error('사진 하나에는 태그를 최대 10개까지 추가할 수 있어요'); return; }

    const spaceNames = (commonCodes?.['A01_SPACE_TAG'] ?? []).map(c => c.codeName);
    const workNames = appCodes.filter(c => c.codeGroupCode === 'ZEU_WORK_TYPE').map(c => c.codeName);
    const tagType = spaceNames.includes(val) ? 'SPACE' : workNames.includes(val) ? 'WORK_TYPE' : 'CUSTOM';
    const tagCode = tagType === 'SPACE' ? spaceCodeByName[val]
      : tagType === 'WORK_TYPE' ? workCodeByName[val]
      : (userTagIdByName[val] != null ? String(userTagIdByName[val]) : undefined);

    const newTags = [...photo.tags, { tagType, tagValue: val, tagCode }];
    const snapshotPhotos = photos;
    const snapshotTags = tags;

    setPhotos(prev => prev.map(p =>
      p.photoId !== photoId ? p : { ...p, tags: newTags }
    ));
    setTags(prev => {
      const existing = prev.find(t => t.tagValue === val);
      if (existing) return prev.map(t => t.tagValue === val ? { ...t, photoCount: t.photoCount + 1 } : t);
      return [...prev, { tagType, tagValue: val, photoCount: 1 }].sort((a, b) => a.tagValue.localeCompare(b.tagValue, 'ko'));
    });

    try {
      await photoApi.patchPhotoTags(photoId, newTags);
    } catch {
      setPhotos(snapshotPhotos);
      setTags(snapshotTags);
      toast.error('태그 저장에 실패했습니다');
    }
  };

  const removePhotoTag = async (photoId: number, tagValue: string) => {
    const photo = photos.find(p => p.photoId === photoId);
    if (!photo) return;

    const newTags = photo.tags.filter(t => t.tagValue !== tagValue);
    const snapshotPhotos = photos;
    const snapshotTags = tags;

    setPhotos(prev => prev.map(p =>
      p.photoId !== photoId ? p : { ...p, tags: newTags }
    ));
    setTags(prev =>
      prev
        .map(t => t.tagValue === tagValue ? { ...t, photoCount: t.photoCount - 1 } : t)
        .filter(t => t.photoCount > 0)
    );

    try {
      await photoApi.patchPhotoTags(photoId, newTags);
    } catch {
      setPhotos(snapshotPhotos);
      setTags(snapshotTags);
      toast.error('태그 삭제에 실패했습니다');
    }
  };

  const handleShareSelected = () => {
    const selected: ShareSelectedPhoto[] = photos
      .filter(p => selectedIds.has(p.photoId))
      .map((p, i) => ({
        id: p.photoId,
        siteId: siteId,
        siteName: detail?.siteName ?? null,
        date: fmtShortDate(p.takenAt),
        registeredAt: p.registeredAt,
        thumbUrl: p.thumbUrl,
        thumbColor: THUMB_COLORS[i % THUMB_COLORS.length],
        tags: p.tags.map(t => t.tagValue),
      }));
    const siteInfo: ShareSiteInfo | null = detail
      ? { siteId: detail.siteId, siteName: detail.siteName, siteAddress: detail.address, customerName: detail.customerName, customerMobile: detail.customerMobile, siteStatus: detail.siteStatus }
      : null;
    setShareSend(selected, siteInfo, 'site-detail', 'photos');
    navigate(`${BASE}/share-send`);
  };

  // 태그 등록 화면은 현장의 사진이 전부 있어야 하므로, 목록 스크롤로 아직 불러오지 않은 페이지까지 전량 조회한다.
  const handleGoToTagRegistration = async () => {
    if (!siteId || !detail || movingToTagEdit) return;
    setMovingToTagEdit(true);
    try {
      const all: SitePhotoItem[] = [];
      let off = 0;
      const pageLimit = 100;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await apiClient.get(`${photoBase}/sites/${siteId}/photos`, { params: { limit: pageLimit, offset: off } });
        const pData: SitePhotoPageResponse = res.data?.data;
        const loaded: SitePhotoItem[] = pData?.photos ?? [];
        all.push(...loaded);
        off += loaded.length;
        if (!pData?.hasNext || loaded.length === 0) break;
      }

      const group: TagEditSiteGroup = {
        siteId,
        siteName: detail.siteName,
        totalOnSite: all.length,
        thumbPhotos: all.map(p => ({ photoId: p.photoId, thumbUrl: p.thumbUrl, takenAt: p.takenAt, registeredAt: p.registeredAt })),
        sessionPhotoIds: all.map(p => p.photoId),
      };
      const savedTags: Record<number, string[]> = {};
      all.forEach(p => { savedTags[p.photoId] = p.tags.map(t => t.tagValue); });

      setTagEditGroups([group]);
      setTagBulkEditSavedTags(savedTags);
      setTagEditReturnPath(`${BASE}/site/${siteId}`);
      navigate(`${BASE}/tag-bulk-edit`);
    } catch {
      toast.error('태그 등록 화면으로 이동하지 못했습니다');
    } finally {
      setMovingToTagEdit(false);
    }
  };

  // 촬영일·등록일·메모는 목록 API가 이미 응답에 포함하고 있어 백엔드 변경 없이 클라이언트에서 거를 수 있다.
  // 단, 지금까지 스크롤로 불러온 사진(photos)에만 적용되므로 아직 안 불러온 뒷페이지는 이 필터를 못 거친 채 남아있다
  // — loadMore가 끝까지 돌아야(hasNext=false) 전체 결과가 정확해진다.
  const extraFilterActive = !!(
    takenDateRange.start || takenDateRange.end || regDateRange.start || regDateRange.end
    || memoFilter !== 'all' || memoQuery.trim()
  );

  const filtered = extraFilterActive
    ? photos.filter(p => {
        if (takenDateRange.start && (!p.takenAt || new Date(p.takenAt) < takenDateRange.start)) return false;
        if (takenDateRange.end && (!p.takenAt || new Date(p.takenAt) > endOfDay(takenDateRange.end))) return false;
        if (regDateRange.start && (!p.registeredAt || new Date(p.registeredAt) < regDateRange.start)) return false;
        if (regDateRange.end && (!p.registeredAt || new Date(p.registeredAt) > endOfDay(regDateRange.end))) return false;
        const hasMemo = !!p.memo?.trim();
        if (memoFilter === 'has' && !hasMemo) return false;
        if (memoFilter === 'none' && hasMemo) return false;
        const q = memoQuery.trim().toLowerCase();
        if (q && !(p.memo ?? '').toLowerCase().includes(q)) return false;
        return true;
      })
    : photos;


  const emptyTitle = extraFilterActive
    ? '조건에 맞는 사진이 없습니다.'
    : selectedTags.size === 0 ? '등록된 사진이 없습니다.' : `"${[...selectedTags].join(', ')}" 태그 사진이 없습니다.`;
  const emptyDesc = extraFilterActive
    ? (hasNext ? '아직 불러오지 않은 사진이 남아있어요 — 스크롤하면 계속 확인합니다.' : '검색·기간 조건을 조정하거나 초기화해 보세요.')
    : selectedTags.size === 0 ? '사진을 업로드하면 여기에 표시됩니다.' : '다른 태그를 선택하거나 사진에 태그를 추가해 주세요.';

  const allSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.photoId));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filtered.map(p => p.photoId)));
  };

  const toggleSelect = (photoId: number, e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
      return next;
    });
  };

  const statusInfo = detail?.siteStatus
    ? (STATUS_MAP[detail.siteStatus] ?? STATUS_MAP['ACTIVE'])
    : null;

  if (!siteId) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--shell-bg)' }}>
        <span style={{ fontSize: 'var(--type-body-md-size)', color: 'var(--shell-on-surface-2)' }}>현장을 선택해주세요.</span>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, position: 'relative' }}>
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0, minHeight: 0, background: 'var(--shell-bg)', padding: '32px 80px 64px', display: 'flex', flexDirection: 'column', gap: 16, opacity: switchingSite ? 0.4 : 1, transition: 'opacity .2s', pointerEvents: switchingSite ? 'none' : 'auto' }}>
          {/* 스크롤 맨 위로(D-09 §15.55) — 이 div 가 스크롤 소유자라 main 의 버튼이 반응하지 않는다. 직속 자식이라 스스로 이 div 를 찾는다 */}
          <ScrollTopButton />

        {/* ── 현장 정보 카드 ── */}
        <div style={{ background: 'linear-gradient(135deg,var(--gt-subtle) 0%,var(--shell-surface) 100%)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', boxShadow: 'var(--elevation-2)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => embedded ? onClose?.() : navigate(`${BASE}/site-list`)}
                aria-label={embedded ? '닫기' : '뒤로'}
                style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--shell-border)', background: 'var(--shell-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              {loading ? (
                <div style={{ width: 60, height: 22, borderRadius: 'var(--radius-full)', background: 'var(--shell-skeleton)' }} />
              ) : statusInfo ? (
                <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, padding: '4px 12px', borderRadius: 'var(--radius-full)', background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.border}` }}>
                  {detail?.siteStatusName ?? detail?.siteStatus}
                </span>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="etc"
                onClick={() => {
                  const site: ShareSiteInfo = detail
                    ? { siteId: detail.siteId, siteName: detail.siteName, siteAddress: detail.address, customerName: detail.customerName, customerMobile: detail.customerMobile, siteStatus: detail.siteStatus }
                    : { siteId: siteId ?? 0, siteName: '', siteAddress: null, customerName: null, customerMobile: null, siteStatus: null };
                  const sitePhotos: ShareSelectedPhoto[] = photos.map((p, i) => ({
                    id: p.photoId,
                    siteId: siteId,
                    siteName: site.siteName,
                    date: fmtShortDate(p.takenAt),
                    registeredAt: p.registeredAt,
                    thumbUrl: p.thumbUrl,
                    thumbColor: THUMB_COLORS[i % THUMB_COLORS.length],
                    tags: p.tags.map(t => t.tagValue),
                  }));
                  setShareSend(sitePhotos, site, 'site-detail', 'site');
                  navigate(`${BASE}/share-send`);
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                현장 공유
              </Button>
              <Button variant="etc" loading={movingToTagEdit} onClick={() => void handleGoToTagRegistration()}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                태그 등록
              </Button>
              <FileInput
                accept=".jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp"
                multiple
                variant="etc"
                disabled={uploading}
                onFiles={handleAddPhotos}
              >
                <>
                  {uploading ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  )}
                  {uploading ? '업로드 중...' : '사진 추가'}
                </>
              </FileInput>
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ width: '55%', height: 24, borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)' }} />
              <div style={{ width: '35%', height: 16, borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)' }} />
              <div style={{ width: '65%', height: 16, borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)' }} />
            </div>
          ) : (
            <>
              <div style={{ fontSize: 'var(--type-title-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 6 }}>
                {detail?.siteName ?? '-'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>고객명 : {detail?.customerName ?? '-'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>{detail?.address ?? '-'}</span>
              </div>
            </>
          )}

          <div style={{ borderTop: '1px solid var(--shell-border)', marginTop: 16, paddingTop: 14, display: 'flex', gap: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 'var(--type-title-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>
                {loading ? '-' : (detail?.photoCount ?? 0)}
                <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 500, color: 'var(--shell-on-surface-2)', marginLeft: 2 }}>장</span>
              </span>
              <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>전체사진</span>
            </div>
            <div style={{ width: 1, background: 'var(--shell-border)', margin: '4px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 'var(--type-title-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>
                {loading ? '-' : tags.length}
                <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 500, color: 'var(--shell-on-surface-2)', marginLeft: 2 }}>개</span>
              </span>
              <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>전체 태그</span>
            </div>
          </div>
        </div>

        {/* ── 검색/필터 카드 ── */}
        <div style={{ background: 'var(--shell-bg)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Search
            value={memoQuery}
            onChange={v => setMemoQuery(v.slice(0, 100))}
            maxLength={100}
            onClear={() => setMemoQuery('')}
            placeholder="메모 내용 검색"
            role="searchbox"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' as const }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>촬영일</span>
              <DateRangePicker value={takenDateRange} onChange={setTakenDateRange} size="sm" aria-label="촬영일 기간 선택" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>등록일</span>
              <DateRangePicker value={regDateRange} onChange={setRegDateRange} size="sm" aria-label="등록일 기간 선택" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>메모</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {([['all', '전체'], ['has', '있음'], ['none', '없음']] as const).map(([key, label]) => (
                  <FilterChip key={key} active={memoFilter === key} onClick={() => setMemoFilter(key)}>
                    {label}
                  </FilterChip>
                ))}
              </div>
            </div>
          </div>
          {extraFilterActive && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span style={{ fontSize: 12, color: 'var(--shell-hint)' }}>
                지금까지 불러온 사진 {photos.length}장 중 {filtered.length}장이 조건에 맞아요.
              </span>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--shell-border)' }} />

          {/* 태그 필터 칩 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {([
              '전체',
              ...(detail?.untaggedPhotoCount ? [UNTAGGED_TAG] : []),
              ...tags.map(t => t.tagValue),
            ]).map(tag => {
              const isActive = tag === '전체' ? selectedTags.size === 0 : selectedTags.has(tag);
              const count = tag === UNTAGGED_TAG
                ? detail?.untaggedPhotoCount
                : tags.find(t => t.tagValue === tag)?.photoCount;
              return (
                <FilterChip
                  key={tag}
                  active={isActive}
                  count={tag !== '전체' ? count ?? undefined : undefined}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </FilterChip>
              );
            })}
          </div>
        </div>

        {/* ── 사진 목록 ── */}
        <div>

          {/* 선택 행 */}
          <div style={{ padding: '8px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectedIds.size > 0 && (
              <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)' }}>{selectedIds.size}장 선택됨</span>
            )}
            <div style={{ flex: 1 }} />
            <Button variant={allSelected ? 'secondary' : 'etc'} onClick={toggleSelectAll}>
              {allSelected ? '전체 해제' : '전체 선택'}
            </Button>
            <Button variant="etc" disabled={selectedIds.size === 0} onClick={handleShareSelected}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              선택 사진 공유
            </Button>
          </div>

          {/* 사진 그리드 */}
          <div style={{ padding: '14px 18px 18px' }}>
            {fetchError && !loading ? (
              <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--color-error)' }}>현장 정보를 불러오지 못했습니다.</div>
                <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>잠시 후 다시 시도해 주세요.</div>
              </div>
            ) : loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ background: 'var(--shell-skeleton)', borderRadius: 'var(--radius-lg)', height: 160 }} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
                <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>
                  {emptyTitle}
                </div>
                <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>
                  {emptyDesc}
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, opacity: isPending ? 0.4 : 1, transition: 'opacity .2s', pointerEvents: isPending ? 'none' : 'auto' }}>
                {filtered.map((photo, idx) => {
                  const goToDetail = () => {
                    setPhotoDetailCtx({ photos: filtered, initialIndex: idx, siteId, siteName: detail?.siteName ?? null, address: detail?.address ?? null });
                    navigate(`${BASE}/photo-detail`);
                  };
                  return (
                    <PhotoCard
                      key={photo.photoId}
                      photoId={photo.photoId}
                      thumbUrl={photo.thumbUrl}
                      takenAt={photo.takenAt}
                      registeredAt={photo.registeredAt}
                      date={fmtShortDate(photo.takenAt)}
                      tags={photo.tags.map(t => t.tagValue)}
                      checked={selectedIds.has(photo.photoId)}
                      showDetailHint
                      tagAreaDefaultCursor
                      thumbAreaDefaultCursor
                      onToggle={goToDetail}
                      onCheckboxToggle={() => toggleSelect(photo.photoId, { stopPropagation: () => {} })}
                      onViewPhoto={goToDetail}
                      onAddTag={tag => void addPhotoTag(photo.photoId, tag)}
                      onRemoveTag={tag => void removePhotoTag(photo.photoId, tag)}
                    />
                  );
                })}
              </div>
            )}

            <div ref={bottomRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', color: 'var(--shell-hint)', fontSize: 'var(--type-body-sm-size)' }}>
                불러오는 중...
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
